import React from 'react';
import {
  type FormData,
  getSubTableRowIndices,
  isOptionMatch,
} from './fieldMapping';
import './interview-form.css';

interface InterviewFormProps {
  data: FormData;
}

/** 复选框：根据值判断是否选中 */
function Checkbox({ label, checked }: { label: string; checked: boolean }) {
  return (
    <span className="checkbox-group">
      <span className={`checkbox-box${checked ? ' checked' : ''}`} />
      {label}
    </span>
  );
}

function LabelValue({ label, value }: { label: string; value?: string }) {
  return (
    <>
      <td className="label-cell">{label}</td>
      <td className="value-cell">{value || ''}</td>
    </>
  );
}

export default function InterviewForm({ data }: InterviewFormProps) {
  const d = data;
  const familyIndices = getSubTableRowIndices(d, 'family');
  const workIndices = getSubTableRowIndices(d, 'work');
  const eduIndices = getSubTableRowIndices(d, 'edu');

  return (
    <div className="interview-form">
      {/* 公司名称（无 Logo 图） */}
      <div className="form-header">
        <div className="form-logo">
          <div>
            <div className="form-company-name">壹鹿有你国际物流</div>
            <div className="form-company-en">YILUYOUNIGUOJIWULIU</div>
          </div>
        </div>
      </div>

      {/* 标题 */}
      <div className="form-title">面试登记表</div>

      {/* ===== 基本信息区（9列网格：每字段组 = 标签1列 + 值2列） ===== */}
      <table className="form-table">
        <colgroup>
          <col style={{ width: '8%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '24%' }} />
        </colgroup>
        <tbody>
          <tr>
            <td className="label-cell">应聘岗位</td>
            <td className="value-cell" colSpan={2}>{d.position}</td>
            <td className="label-cell">可到岗时间</td>
            <td className="value-cell" colSpan={2}>{d.availableDate}</td>
            <td className="label-cell">期望薪资</td>
            <td className="value-cell" colSpan={2}>{d.expectedSalary}</td>
          </tr>
          <tr>
            <td className="label-cell">姓名</td>
            <td className="value-cell" colSpan={2}>{d.name}</td>
            <td className="label-cell">邀约HR</td>
            <td className="value-cell" colSpan={2}>{d.inviteHr}</td>
            <td className="label-cell">民族</td>
            <td className="value-cell" colSpan={2}>{d.ethnicity}</td>
          </tr>
          <tr>
            <td className="label-cell">联系电话</td>
            <td className="value-cell" colSpan={2}>{d.phone}</td>
            <td className="label-cell">出生日期</td>
            <td className="value-cell" colSpan={2}>{d.birthDate}</td>
            <td className="label-cell">体重</td>
            <td className="value-cell" colSpan={2}>{d.weight}</td>
          </tr>
          <tr>
            <td className="label-cell">身高</td>
            <td className="value-cell" colSpan={2}>{d.height}</td>
            <td className="label-cell">健康状况</td>
            <td className="value-cell" colSpan={2}>{d.healthStatus}</td>
            <td className="label-cell">籍贯</td>
            <td className="value-cell" colSpan={2}>{d.nativePlace}</td>
          </tr>
          <tr>
            <td className="label-cell">婚姻状况</td>
            <td className="value-cell" colSpan={4}>
              <Checkbox label="已婚" checked={isOptionMatch(d.maritalStatus, '已婚')} />
              <Checkbox label="未婚" checked={isOptionMatch(d.maritalStatus, '未婚')} />
              <Checkbox label="已育" checked={isOptionMatch(d.maritalStatus, '已育')} />
            </td>
            <td className="label-cell">邮箱</td>
            <td className="value-cell" colSpan={3}>{d.email}</td>
          </tr>
          <tr>
            <td className="label-cell">户籍地址</td>
            <td className="value-cell" colSpan={8}>{d.hukouAddress}</td>
          </tr>
          <tr>
            <td className="label-cell">现居住址</td>
            <td className="value-cell" colSpan={8}>{d.currentAddress}</td>
          </tr>
        </tbody>
      </table>

      {/* ===== 家庭信息 ===== */}
      <table className="form-table sub-table">
        <thead>
          <tr>
            <th colSpan={6} className="section-title">家庭信息</th>
          </tr>
          <tr>
            <th>姓名</th>
            <th>关系</th>
            <th>年龄</th>
            <th>工作单位</th>
            <th>岗位</th>
            <th>家庭住址</th>
          </tr>
        </thead>
        <tbody>
          {familyIndices.map((i) => (
            <tr key={i} className="data-row">
              <td>{d[`family_${i}_name`]}</td>
              <td>{d[`family_${i}_relation`]}</td>
              <td>{d[`family_${i}_age`]}</td>
              <td>{d[`family_${i}_employer`]}</td>
              <td>{d[`family_${i}_position`]}</td>
              <td>{d[`family_${i}_address`]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== 紧急联系人 & 职业状态 ===== */}
      <table className="form-table">
        <tbody>
          <tr className="emergency-contact-row">
            <LabelValue label="紧急联络人" value={d.emergencyContact} />
            <LabelValue label="关系" value={d.emergencyRelation} />
            <LabelValue label="联系电话" value={d.emergencyPhone} />
          </tr>
          <tr>
            <td className="label-cell">职业状态</td>
            <td className="value-cell" colSpan={2}>
              <Checkbox label="在职" checked={isOptionMatch(d.employmentStatus, '在职')} />
              <Checkbox label="待业" checked={isOptionMatch(d.employmentStatus, '待业')} />
              <Checkbox label="兼职" checked={isOptionMatch(d.employmentStatus, '兼职')} />
            </td>
            <td className="label-cell">出差意愿调查</td>
            <td className="value-cell" colSpan={2}>
              <Checkbox label="可接受" checked={isOptionMatch(d.travelWillingness, '可接受')} />
              <Checkbox label="不能接受" checked={isOptionMatch(d.travelWillingness, '不能接受')} />
            </td>
          </tr>
          <tr>
            <td className="label-cell" colSpan={2} style={{ textAlign: 'left' }}>
              是否有亲戚、朋友、熟人在本公司任职?
            </td>
            <td className="value-cell">
              <Checkbox label="无" checked={isOptionMatch(d.hasRelativeInCompany, '无')} />
              <Checkbox label="有" checked={isOptionMatch(d.hasRelativeInCompany, '有')} />
            </td>
            <td className="label-cell">姓名</td>
            <td className="value-cell" colSpan={2}>{d.relativeName}</td>
          </tr>
          <tr>
            <td className="label-cell">和原单位劳动关系?</td>
            <td className="value-cell" colSpan={2}>
              <Checkbox label="已解除" checked={isOptionMatch(d.laborRelation, '已解除')} />
              <Checkbox label="未解除" checked={isOptionMatch(d.laborRelation, '未解除')} />
            </td>
            <td className="label-cell confidentiality-label">是否签订保密或竞业协议?</td>
            <td className="value-cell" colSpan={2}>
              <Checkbox label="是" checked={isOptionMatch(d.confidentialityAgreement, '是')} />
              <Checkbox label="否" checked={isOptionMatch(d.confidentialityAgreement, '否')} />
            </td>
          </tr>
        </tbody>
      </table>

      {/* ===== 工作经历 ===== */}
      <table className="form-table sub-table">
        <thead>
          <tr>
            <th colSpan={6} className="section-title">
              工作经历, 由远及近填写:
            </th>
          </tr>
          <tr>
            <th>任职时间 (起/止)</th>
            <th>公司名称</th>
            <th>职位</th>
            <th>薪资</th>
            <th>离职原因</th>
            <th>证明人及电话</th>
          </tr>
        </thead>
        <tbody>
          {workIndices.map((i) => (
            <tr key={i} className="data-row">
              <td>{d[`work_${i}_period`]}</td>
              <td>{d[`work_${i}_company`]}</td>
              <td>{d[`work_${i}_position`]}</td>
              <td>{d[`work_${i}_salary`]}</td>
              <td>{d[`work_${i}_reason`]}</td>
              <td>{d[`work_${i}_reference`]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== 教育经历 ===== */}
      <table className="form-table sub-table">
        <thead>
          <tr>
            <th colSpan={4} className="section-title">
              教育经历, 由远及近填写:
            </th>
          </tr>
          <tr>
            <th>学习时间 (起/止)</th>
            <th>学校名称</th>
            <th>专业</th>
            <th>学历</th>
          </tr>
        </thead>
        <tbody>
          {eduIndices.map((i) => (
            <tr key={i} className="data-row">
              <td>{d[`edu_${i}_period`]}</td>
              <td>{d[`edu_${i}_school`]}</td>
              <td>{d[`edu_${i}_major`]}</td>
              <td>{d[`edu_${i}_degree`]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== 身体状况 ===== */}
      <table className="form-table">
        <tbody>
          <tr>
            <td className="section-title" colSpan={4} style={{ textAlign: 'left' }}>身体状况:</td>
          </tr>
          <tr>
            <td colSpan={3} style={{ textAlign: 'left', padding: '4px 6px' }}>
              目前是否有身孕，或近一年是否有怀孕计划（适用于女性应聘者）？
            </td>
            <td className="value-cell" style={{ textAlign: 'center' }}>
              <Checkbox label="是" checked={isOptionMatch(d.pregnancyPlan, '是')} />
              <Checkbox label="否" checked={isOptionMatch(d.pregnancyPlan, '否')} />
            </td>
          </tr>
          <tr>
            <td colSpan={3} style={{ textAlign: 'left', padding: '4px 6px' }}>
              是否有家庭遗传病史或重大病史？
            </td>
            <td className="value-cell" style={{ textAlign: 'center' }}>
              <Checkbox label="是" checked={isOptionMatch(d.familyMedicalHistory, '是')} />
              <Checkbox label="否" checked={isOptionMatch(d.familyMedicalHistory, '否')} />
            </td>
          </tr>
        </tbody>
      </table>

      {/* ===== 应聘声明 ===== */}
      <div className="declaration-section">
        <div className="declaration-title">【应聘声明】</div>
        <div className="declaration-text">
          1、本人保证以上所填各项内容的真实性，如有虚假，愿承担相应责任。
        </div>
        <div className="declaration-text">
          2、本人同意公司对以上信息进行背景调查，并授权公司提供相关信息。
        </div>
        <div className="declaration-footer">
          <span>
            应聘人签字：<span className="sign-line">{d.signature}</span>
          </span>
          <span>
            面试日期：<span className="sign-line">{d.interviewDate}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
